'use client';

import { type FC } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Image } from '@packages/shared/src/ui/Image/Image';
import type { ResponsiveNavigationItemBaseProps } from './ResponsiveNavigationItemBase';
import { ResponsiveNavigationItemBase } from './ResponsiveNavigationItemBase';
import { responsiveNavigationMainItemVariants } from './ResponsiveNavigationMainItem.variants';

export type ResponsiveNavigationMainItemProps = Omit<
  ResponsiveNavigationItemBaseProps,
  'listItemClassNames' | 'linkClassNames'
> & {
  /** If `true`, the category corresponding to the item is the current page */
  isActive?: boolean;
  /** If `true`, the children of the item are displayed in the nav popup */
  isOpen?: boolean;
  imageUrl?: string;
};

/**
 * Navigation item for the main navigation (always visible)
 */
export const ResponsiveNavigationMainItem: FC<ResponsiveNavigationMainItemProps> = ({
  isActive,
  isOpen,
  imageUrl,
  children,
  onClick,
  onKeyDown,
  ...baseProps
}) => {
  const {
    header: { hasSmallerFontSize },
  } = useConfig();

  return (
    <ResponsiveNavigationItemBase
      onKeyDown={onKeyDown}
      onClick={onClick}
      {...baseProps}
      listItemClassNames={responsiveNavigationMainItemVariants({
        active: isActive,
        open: isOpen,
      })}
      linkClassNames="flex-col item-stretch"
    >
      <div className="bg-color-grey-light relative flex flex-1 items-stretch overflow-hidden lg:hidden">
        {imageUrl && (
          <Image
            src={imageUrl}
            // decorative image in line with WAI standards
            alt=""
            fill
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          />
        )}
      </div>
      <span
        //  custom fontSizes on large screens are added as per UX/UI requirements.
        className={`${hasSmallerFontSize ? 'lg:text-[0.93rem] xl:text-[1.06rem]' : 'lg:text-size-body-base xl:text-[1.125rem]'} px-1 py-0.5 leading-2.5 lg:py-1.5 lg:text-center lg:whitespace-nowrap`}
      >
        {children}
      </span>
    </ResponsiveNavigationItemBase>
  );
};
